import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Task } from '@/services/task';

interface TaskState {
	tasks: Task[];
	lastSyncTime: number;
	/** Task id for which the details sheet is open; null when closed. Not persisted. */
	selectedTaskId: number | null;
	/**
	 * Task to open in the edit dialog; null when closed. Lets a deeply-nested
	 * card (e.g. the Today timeline context menu) open the edit form without
	 * prop drilling — mirrors `meetingToEdit` in the meeting store. Not persisted.
	 */
	taskToEdit: Task | null;
	setTasks: (tasks: Task[]) => void;
	setLastSyncTime: (t: number) => void;
	setSelectedTaskId: (id: number | null) => void;
	setTaskToEdit: (task: Task | null) => void;
	mergeTasks: (delta: Task[]) => void;
	clearTasks: () => void;
}

export const useTaskStore = create<TaskState>()(
	persist(
		(set) => ({
			tasks: [],
			lastSyncTime: 0,
			selectedTaskId: null,
			taskToEdit: null,

			setTasks: (tasks) => set({ tasks }),

			setLastSyncTime: (t) => set({ lastSyncTime: t }),

			setSelectedTaskId: (id) => set({ selectedTaskId: id }),

			setTaskToEdit: (task) => set({ taskToEdit: task }),

			mergeTasks: (delta) =>
				set((state) => {
					const byId = new Map(state.tasks.map((t) => [t.id, t]));
					for (const t of delta) {
						if (t.is_delete === 'Y' || t.is_task_deleted === 'Y') {
							byId.delete(t.id);
						} else {
							const prev = byId.get(t.id);
							if (prev) {
								byId.set(t.id, {
									...prev,
									...t,
									tag: t.tag ?? prev.tag,
									creator: t.creator ?? prev.creator,
									receiver: t.receiver ?? prev.receiver,
									group: t.group ?? prev.group,
									company: t.company ?? prev.company,
									members: t.members ?? prev.members,
								});
							} else {
								byId.set(t.id, t);
							}
						}
					}
					return { tasks: Array.from(byId.values()) };
				}),

			clearTasks: () => set({ tasks: [], lastSyncTime: 0 }),
		}),
		{
			name: 'task-storage',
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({ tasks: state.tasks, lastSyncTime: state.lastSyncTime }),
		}
	)
);

/** Store instance for use outside React (e.g. in queryFn). */
export const taskStore = useTaskStore;
